import React, { useState, useEffect } from 'react';
import { AdminHeader } from './AdminLayout';
import { getSettings, updateSettings } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
    KeyRound, 
    Calendar,
    Save,
    Eye,
    EyeOff,
    Copy,
    UserCircle,
    Music,
    Lock,
    AlertTriangle,
    Shield,
    Trash2,
    Pencil,
    ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

export function EinstellungenAllgemein() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPin, setShowPin] = useState(false);
    
    useEffect(() => {
        fetchSettings();
    }, []);
    
    const fetchSettings = async () => {
        try {
            const response = await getSettings();
            setSettings(response.data);
        } catch (err) {
            console.error('Failed to fetch settings:', err);
            toast.error('Einstellungen konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSave = async () => {
        try {
            setSaving(true);
            await updateSettings(settings);
            toast.success('Einstellungen gespeichert');
        } catch (err) {
            console.error('Failed to save settings:', err);
            toast.error('Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };
    
    const copyPin = () => {
        navigator.clipboard.writeText(settings?.lehrer_pin || '');
        toast.success('PIN kopiert');
    };
    
    if (loading) {
        return (
            <>
                <AdminHeader title="Allgemeine Einstellungen" />
                <div className="admin-content">
                    <div className="max-w-4xl space-y-8">
                        {[1,2].map(i => (
                            <div key={i} className="bg-card rounded-2xl p-6 animate-pulse">
                                <div className="h-6 bg-muted rounded w-48 mb-4" />
                                <div className="h-12 bg-muted rounded w-full" />
                            </div>
                        ))}
                    </div>
                </div>
            </>
        );
    }
    
    return (
        <>
            <AdminHeader title="Allgemeine Einstellungen">
                <Button onClick={handleSave} disabled={saving} className="rounded-xl">
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Speichern...' : 'Speichern'}
                </Button>
            </AdminHeader>
            
            <div className="admin-content">
                <div className="max-w-4xl space-y-8">
                    
                    {/* Lehrer-Portal */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                                <KeyRound className="h-5 w-5 text-violet-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Lehrer-Portal</h2>
                                <p className="text-sm text-muted-foreground">
                                    PIN-Code für den Zugang zum Lehrer-Portal
                                </p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="form-group">
                                <Label className="form-label">Gemeinsamer PIN-Code</Label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1 max-w-xs">
                                        <Input
                                            type={showPin ? "text" : "password"}
                                            value={settings?.lehrer_pin || ''}
                                            onChange={(e) => setSettings({...settings, lehrer_pin: e.target.value})}
                                            className="form-input pr-20 font-mono text-lg tracking-widest"
                                            placeholder="123456"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPin(!showPin)}
                                            className="absolute right-10 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                                        >
                                            {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={copyPin}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                                        >
                                            <Copy className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Dieser PIN gilt für alle Lehrpersonen.
                                </p>
                            </div>
                            <div className="pt-4 border-t">
                                <a href="/lehrer" target="_blank" className="text-primary hover:underline text-sm">
                                    → Lehrer-Portal öffnen
                                </a>
                            </div>
                        </div>
                    </div>
                    
                    {/* Lehrer-Portal Deadline */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                                <Lock className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Eintragungsfrist</h2>
                                <p className="text-sm text-muted-foreground">
                                    Deadline für Lehrer-Eintragungen im Portal
                                </p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="form-group">
                                <Label className="form-label">Deadline-Datum</Label>
                                <Input
                                    type="date"
                                    value={settings?.lehrer_deadline || ''}
                                    onChange={(e) => setSettings({...settings, lehrer_deadline: e.target.value})}
                                    className="form-input max-w-xs"
                                />
                                <p className="text-sm text-muted-foreground mt-2">
                                    Ab diesem Datum sind keine Änderungen mehr möglich.
                                </p>
                            </div>
                            
                            <div className="form-group">
                                <Label className="form-label">Vorwarnzeit (Tage)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="30"
                                    value={settings?.lehrer_deadline_offset ?? 7}
                                    onChange={(e) => setSettings({...settings, lehrer_deadline_offset: parseInt(e.target.value) || 0})}
                                    className="form-input max-w-[100px]"
                                />
                                <p className="text-sm text-muted-foreground mt-2">
                                    Lehrer sehen X Tage vor der Deadline eine Warnung.
                                </p>
                            </div>
                            
                            {/* Preview */}
                            {settings?.lehrer_deadline && (
                                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                                        <div className="text-sm">
                                            <p className="font-medium text-amber-800">Vorschau:</p>
                                            <p className="text-amber-700">
                                                Warnung erscheint ab: <strong>
                                                    {new Date(new Date(settings.lehrer_deadline).getTime() - (settings.lehrer_deadline_offset || 7) * 24 * 60 * 60 * 1000).toLocaleDateString('de-AT')}
                                                </strong>
                                            </p>
                                            <p className="text-amber-700">
                                                Sperre ab: <strong>{new Date(settings.lehrer_deadline).toLocaleDateString('de-AT')}</strong>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Clear deadline button */}
                            {settings?.lehrer_deadline && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSettings({...settings, lehrer_deadline: ''})}
                                    className="text-muted-foreground"
                                >
                                    Deadline entfernen
                                </Button>
                            )}
                        </div>
                    </div>
                    
                    {/* Event Einstellungen */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                                <Calendar className="h-5 w-5 text-red-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Woche des offenen Unterrichts</h2>
                                <p className="text-sm text-muted-foreground">
                                    Datum der Veranstaltung
                                </p>
                            </div>
                        </div>
                        
                        <div className="form-group">
                            <Label className="form-label">Datum (z.B. "24. - 28. März 2025")</Label>
                            <Input
                                type="text"
                                value={settings?.event_dates || ''}
                                onChange={(e) => setSettings({...settings, event_dates: e.target.value})}
                                className="form-input max-w-md"
                                placeholder="24. - 28. März 2025"
                            />
                            <p className="text-sm text-muted-foreground mt-2">
                                Dieses Datum wird auf der Startseite angezeigt.
                            </p>
                        </div>
                        
                        <div className="flex items-center justify-between py-3 mt-4 border-t">
                            <div>
                                <p className="font-medium">Datum neben Wochentag anzeigen</p>
                                <p className="text-sm text-muted-foreground">Zeigt z.B. "Montag, 24. März" statt nur "Montag"</p>
                            </div>
                            <Switch 
                                checked={settings?.show_weekday_dates ?? true}
                                onCheckedChange={(v) => setSettings({...settings, show_weekday_dates: v})}
                            />
                        </div>
                    </div>
                    
                    {/* Öffentliche Anzeige */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                                <UserCircle className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Öffentliche Anzeige</h2>
                                <p className="text-sm text-muted-foreground">
                                    Welche Lehrer-Informationen sollen öffentlich sichtbar sein?
                                </p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center justify-between py-3 border-b">
                                <div>
                                    <p className="font-medium">Foto anzeigen</p>
                                    <p className="text-sm text-muted-foreground">Lehrer-Foto in Ergebnissen</p>
                                </div>
                                <Switch 
                                    checked={settings?.show_teacher_photo ?? true}
                                    onCheckedChange={(v) => setSettings({...settings, show_teacher_photo: v})}
                                />
                            </div>
                            <div className="flex items-center justify-between py-3 border-b">
                                <div>
                                    <p className="font-medium">Telefonnummer anzeigen</p>
                                </div>
                                <Switch 
                                    checked={settings?.show_teacher_phone ?? true}
                                    onCheckedChange={(v) => setSettings({...settings, show_teacher_phone: v})}
                                />
                            </div>
                            <div className="flex items-center justify-between py-3 border-b">
                                <div>
                                    <p className="font-medium">E-Mail anzeigen</p>
                                </div>
                                <Switch 
                                    checked={settings?.show_teacher_email ?? true}
                                    onCheckedChange={(v) => setSettings({...settings, show_teacher_email: v})}
                                />
                            </div>
                            <div className="flex items-center justify-between py-3 border-b">
                                <div>
                                    <p className="font-medium">Andere Standorte anzeigen</p>
                                    <p className="text-sm text-muted-foreground">Zeigt wo der Lehrer noch unterrichtet</p>
                                </div>
                                <Switch 
                                    checked={settings?.show_teacher_other_locations ?? true}
                                    onCheckedChange={(v) => setSettings({...settings, show_teacher_other_locations: v})}
                                />
                            </div>
                            <div className="flex items-center justify-between py-3">
                                <div>
                                    <p className="font-medium">Raum anzeigen</p>
                                    <p className="text-sm text-muted-foreground">Zeigt den Raum-Namen in den Ergebnissen</p>
                                </div>
                                <Switch 
                                    checked={settings?.show_room_info ?? false}
                                    onCheckedChange={(v) => setSettings({...settings, show_room_info: v})}
                                />
                            </div>
                        </div>
                    </div>
                    
                    {/* Lehrerportal Berechtigungen */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                                <Shield className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Lehrerportal Berechtigungen</h2>
                                <p className="text-sm text-muted-foreground">
                                    Was können Lehrer im Lehrerportal machen?
                                </p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center justify-between py-3 border-b">
                                <div className="flex items-start gap-3">
                                    <Pencil className="w-5 h-5 text-blue-500 mt-0.5" />
                                    <div>
                                        <p className="font-medium">Angebote bearbeiten</p>
                                        <p className="text-sm text-muted-foreground">Lehrer können ihre eigenen Angebote ändern</p>
                                    </div>
                                </div>
                                <Switch 
                                    checked={settings?.lehrer_can_edit ?? true}
                                    onCheckedChange={(v) => setSettings({...settings, lehrer_can_edit: v})}
                                />
                            </div>
                            
                            <div className="flex items-center justify-between py-3 border-b">
                                <div className="flex items-start gap-3">
                                    <Trash2 className="w-5 h-5 text-red-500 mt-0.5" />
                                    <div>
                                        <p className="font-medium">Angebote löschen</p>
                                        <p className="text-sm text-muted-foreground">Lehrer können ihre eigenen Angebote löschen</p>
                                    </div>
                                </div>
                                <Switch 
                                    checked={settings?.lehrer_can_delete ?? false}
                                    onCheckedChange={(v) => setSettings({...settings, lehrer_can_delete: v})}
                                />
                            </div>
                            
                            {!settings?.lehrer_can_delete && (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                                    <p className="text-sm text-amber-700">
                                        Lehrer können Angebote nur bearbeiten, nicht löschen. 
                                        Bei Bedarf muss der Admin löschen.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Öffentliche Links (Footer) */}
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                                <ExternalLink className="h-5 w-5 text-emerald-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Öffentliche Links</h2>
                                <p className="text-sm text-muted-foreground">
                                    Links, die im Footer der öffentlichen Seiten angezeigt werden
                                </p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="form-group">
                                <Label className="form-label">Anmeldungs-URL</Label>
                                <Input
                                    type="url"
                                    value={settings?.anmeldung_url || ''}
                                    onChange={(e) => setSettings({...settings, anmeldung_url: e.target.value})}
                                    className="form-input"
                                    placeholder="https://..."
                                />
                                <p className="text-sm text-muted-foreground mt-2">
                                    Link zum Anmeldeformular (z.B. Google Form, Typeform, etc.). 
                                    Wenn leer, wird der "Anmeldung"-Button im Footer ausgeblendet.
                                </p>
                            </div>
                            
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                                <strong>Hinweis:</strong> Der "Kontakt"-Button im Footer zeigt automatisch alle 
                                Lehrpersonen, die als "Administrationskontakt" markiert sind.
                            </div>
                        </div>
                    </div>
                    
                    {/* Instrument-Anzeige - versteckt, kann später wieder aktiviert werden
                    <div className="bg-card rounded-2xl border p-6">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                                <Music className="h-5 w-5 text-violet-600" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold">Instrument-Anzeige</h2>
                                <p className="text-sm text-muted-foreground">
                                    Optionen für die Instrument-Auswahl im Wizard
                                </p>
                            </div>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-center justify-between py-3">
                                <div>
                                    <p className="font-medium">Icons bei Instrumenten anzeigen</p>
                                    <p className="text-sm text-muted-foreground">Zeigt kleine Icons neben jedem Instrument</p>
                                </div>
                                <Switch 
                                    checked={settings?.show_instrument_icons ?? false}
                                    onCheckedChange={(v) => setSettings({...settings, show_instrument_icons: v})}
                                />
                            </div>
                        </div>
                    </div>
                    */}
                </div>
            </div>
        </>
    );
}
